import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import { Box, SxProps } from "@mui/material";
import { ForwardedRef, forwardRef, ReactNode, useEffect, useImperativeHandle, useRef, useState } from "react";
import { throttleFn } from '../../utils';
import { XScrollDirections, YScrollDirections } from './ScrollIndicatorsCompModel';
import { contentWrapperStyle, scrollIndicatorBarStyle, wrapperStyle } from "./styled";

interface ScrollIndicatorsCompProps {
  children?: ReactNode;
  sx?: SxProps;
  hideXAxisArrows?: boolean;
  hideYAxisArrows?: boolean;
  buttonScrollAmount?: number;
}

const ScrollIndicatorsComp = forwardRef((props: ScrollIndicatorsCompProps, ref: ForwardedRef<Element | undefined>) => {
  const { children, sx, hideXAxisArrows, hideYAxisArrows, buttonScrollAmount = 100 } = props;
  const [xScrollState, setXScrollState] = useState(XScrollDirections.None);
  const [yScrollState, setYScrollState] = useState(YScrollDirections.None);
  const resizeObserver = useRef<ResizeObserver | null>(null);
  const mutationObserver = useRef<MutationObserver | null>(null);
  const boxRef = useRef<Element | undefined>();

  useImperativeHandle(ref, () => boxRef.current, [boxRef]);

  const handleScrollClick = (e: React.MouseEvent, direction: 'left' | 'right' | 'up' | 'down') => {
    e.stopPropagation();
    switch (direction) {
      case 'left': boxRef.current?.scrollBy({ left: buttonScrollAmount, behavior: 'smooth' });
        break;
      case 'right': boxRef.current?.scrollBy({ left: -buttonScrollAmount, behavior: 'smooth' });
        break;
      case 'up': boxRef.current?.scrollBy({ top: buttonScrollAmount, behavior: 'smooth' });
        break;
      case 'down': boxRef.current?.scrollBy({ top: -buttonScrollAmount, behavior: 'smooth' });
        break;
    }
  }

  const calcXScrollState = (proportion: number): XScrollDirections => {
    if (proportion <= 0.01) return XScrollDirections.Left;
    else if (proportion >= 0.99) return XScrollDirections.Right;
    return XScrollDirections.Both;
  }

  const calcYScrollState = (proportion: number): YScrollDirections => {
    if (proportion <= 0.01) return YScrollDirections.Up;
    else if (proportion >= 0.99) return YScrollDirections.Down;
    return YScrollDirections.Both;
  }

  const handleScrollIndication = () => {
    const handleXScrolling = () => {
      if (boxRef.current) {
        if (boxRef.current.scrollWidth - boxRef.current.clientWidth === 0) {
          setXScrollState(XScrollDirections.None);
          return;
        }
        const proportionX = boxRef.current.scrollLeft / (boxRef.current.scrollWidth - boxRef.current.clientWidth);
        setXScrollState(calcXScrollState(proportionX || 0));
      }
    }

    const handleYScrolling = () => {
      if (boxRef.current) {
        if (boxRef.current.scrollHeight - boxRef.current.clientHeight === 0) {
          setYScrollState(YScrollDirections.None);
          return;
        }
        const proportionY = boxRef.current.scrollTop / (boxRef.current.scrollHeight - boxRef.current.clientHeight);
        setYScrollState(calcYScrollState(proportionY || 0));
      }
    }

    if (!hideXAxisArrows) handleXScrolling();
    if (!hideYAxisArrows) handleYScrolling();
  }

  const ScrollIndicatorBar = ({ sx, isTop }: { sx?: SxProps, isTop?: boolean }) => {
    const YDirectionArrows = () => {
      return (
        isTop ?
          <KeyboardDoubleArrowUpIcon
            sx={{
              opacity: yScrollState & (YScrollDirections.Down | YScrollDirections.Both) ? 1 : 0,
            }}
            onClick={(e) => handleScrollClick(e, 'down')}
          /> :
          <KeyboardDoubleArrowDownIcon
            sx={{
              opacity: yScrollState & (YScrollDirections.Up | YScrollDirections.Both) ? 1 : 0,
            }}
            onClick={(e) => handleScrollClick(e, 'up')}
          />
      );
    }

    return (
      <Box sx={{ ...scrollIndicatorBarStyle, ...sx } as SxProps}>
        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
          {!hideXAxisArrows && <KeyboardDoubleArrowLeftIcon
            sx={{
              opacity: xScrollState & (XScrollDirections.Right | XScrollDirections.Both) ? 1 : 0,
            }}
            onClick={(e) => handleScrollClick(e, 'right')}
          />}
          {!hideYAxisArrows && <YDirectionArrows />}
        </Box>
        <Box component="span" sx={{ flex: 1 }} />
        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
          {!hideYAxisArrows && <YDirectionArrows />}
          {!hideXAxisArrows && <KeyboardDoubleArrowRightIcon
            sx={{
              opacity: xScrollState & (XScrollDirections.Left | XScrollDirections.Both) ? 1 : 0,
            }}
            onClick={(e) => handleScrollClick(e, 'left')}
          />}
        </Box>
      </Box>
    );
  }

  /**
   * Handles the display of the arrows when:
   * - child node changes
   * - container size
   * - scroll interactions
   */
  useEffect(() => {
    if (!boxRef.current) return;
    const handleDisplayArrows = throttleFn(handleScrollIndication);
    mutationObserver.current = new MutationObserver(handleDisplayArrows);
    mutationObserver.current.observe(boxRef.current, { childList: true });
    resizeObserver.current = new ResizeObserver(handleDisplayArrows);
    resizeObserver.current.observe(boxRef.current);
    boxRef.current.addEventListener('scroll', handleDisplayArrows, { passive: true });
    handleScrollIndication(); // set initial states
    return () => {
      mutationObserver.current?.disconnect();
      resizeObserver.current?.disconnect();
      boxRef.current?.removeEventListener('scroll', handleDisplayArrows);
    }
  }, [boxRef.current]);

  return (
    <Box sx={wrapperStyle}>
      <Box sx={{ py: (yScrollState | xScrollState) ? 0.5 : 0 }}>
        <ScrollIndicatorBar sx={{ mb: 0.5 }} isTop />
        <Box ref={boxRef} sx={{ ...sx, ...contentWrapperStyle }}>
          {children}
        </Box>
        <ScrollIndicatorBar sx={{ mt: 0.5 }} />
      </Box>
    </Box>
  );
});

export default ScrollIndicatorsComp;
